import api from "../api/axios";
import { MAX_REPLAY_RANGE_MS, normalizeReplayObservation } from "./gpsReplaySnapshot";

export const MAX_MATCHED_REPLAY_RANGE_MS = Math.min(MAX_REPLAY_RANGE_MS, 24 * 60 * 60 * 1000);
const STATUSES = new Set(["raw", "matched", "partially_matched", "insufficient_coverage", "unavailable", "failed"]);

export class GpsReplayGeometrySnapshotError extends Error {
  constructor(code) {
    super("GPS replay geometry could not be loaded safely.");
    this.name = "GpsReplayGeometrySnapshotError";
    this.code = code;
  }
}

function validDate(value) {
  const parsed = value instanceof Date ? new Date(value) : new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function validCoordinate(value) {
  return value && Number.isFinite(Number(value.latitude)) && Number(value.latitude) >= -90 &&
    Number(value.latitude) <= 90 && Number.isFinite(Number(value.longitude)) &&
    Number(value.longitude) >= -180 && Number(value.longitude) <= 180;
}

function normalizeContract(body, observations, rangeFrom, rangeTo) {
  const responseFrom = validDate(body?.range?.from);
  const responseTo = validDate(body?.range?.to);
  if (!body || body.source !== "physical" || !["raw_gps", "osrm"].includes(body.requestedProvider) ||
      !["raw_gps", "osrm"].includes(body.geometryProvider) || !STATUSES.has(body.status) ||
      body.observationCount !== observations.length || !Array.isArray(body.matchedSegments) ||
      !Array.isArray(body.sourceObservationAnchors) || !Array.isArray(body.gaps) ||
      !Array.isArray(body.dwells) || !Number.isFinite(body.observedGpsDistanceMeters) ||
      body.observedGpsDistanceMeters < 0 ||
      !(body.matchedRoadDistanceMeters === null || body.matchedRoadDistanceMeters === undefined ||
        (Number.isFinite(body.matchedRoadDistanceMeters) && body.matchedRoadDistanceMeters >= 0)) ||
      !responseFrom || !responseTo || responseFrom.getTime() !== rangeFrom.getTime() ||
      responseTo.getTime() !== rangeTo.getTime() ||
      !body.fallback || typeof body.fallback.used !== "boolean") {
    throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_CONTRACT_INVALID");
  }
  const matchedSegments = body.matchedSegments.map((segment, index) => {
    if (!segment || !Array.isArray(segment.coordinates) || segment.coordinates.length < 2 ||
        !Number.isFinite(segment.distanceMeters) || segment.distanceMeters < 0 ||
        !(segment.providerConfidence === null || segment.providerConfidence === undefined ||
          (Number.isFinite(segment.providerConfidence) && segment.providerConfidence >= 0 &&
            segment.providerConfidence <= 1)) ||
        segment.coordinates.some((point) => !validCoordinate(point))) {
      throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_CONTRACT_INVALID");
    }
    return Object.freeze({
      id: String(segment.id || `matched-${index}`).slice(0, 80),
      coordinates: Object.freeze(segment.coordinates.map((point) => Object.freeze({
        latitude: Number(point.latitude), longitude: Number(point.longitude),
      }))),
      distanceMeters: segment.distanceMeters,
      providerConfidence: Number.isFinite(segment.providerConfidence) &&
        segment.providerConfidence >= 0 && segment.providerConfidence <= 1
        ? segment.providerConfidence : null,
    });
  });
  const anchors = body.sourceObservationAnchors.map((anchor) => {
    if (!anchor || !Number.isSafeInteger(anchor.sourceObservationIndex) ||
        anchor.sourceObservationIndex < 0 || anchor.sourceObservationIndex >= observations.length ||
        !Number.isSafeInteger(anchor.matchedSegmentIndex) || anchor.matchedSegmentIndex < 0 ||
        anchor.matchedSegmentIndex >= matchedSegments.length || !validCoordinate(anchor.matchedCoordinates)) {
      throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_CONTRACT_INVALID");
    }
    return Object.freeze({
      sourceObservationIndex: anchor.sourceObservationIndex,
      matchedSegmentIndex: anchor.matchedSegmentIndex,
      waypointIndex: Number.isSafeInteger(anchor.waypointIndex) ? anchor.waypointIndex : null,
      matchedCoordinates: Object.freeze({
        latitude: Number(anchor.matchedCoordinates.latitude),
        longitude: Number(anchor.matchedCoordinates.longitude),
      }),
    });
  });
  if (body.status === "matched" && (body.geometryProvider !== "osrm" || !matchedSegments.length || !anchors.length)) {
    throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_CONTRACT_INVALID");
  }
  return Object.freeze({
    requestedProvider: body.requestedProvider,
    geometryProvider: body.geometryProvider,
    status: body.status,
    providerVersion: typeof body.providerVersion === "string" ? body.providerVersion.slice(0, 80) : null,
    geometryVersion: typeof body.geometryVersion === "string" ? body.geometryVersion.slice(0, 80) : null,
    matchedSegments: Object.freeze(matchedSegments),
    sourceObservationAnchors: Object.freeze(anchors),
    gaps: Object.freeze([...body.gaps]),
    dwells: Object.freeze([...body.dwells]),
    observedGpsDistanceMeters: body.observedGpsDistanceMeters,
    matchedRoadDistanceMeters: Number.isFinite(body.matchedRoadDistanceMeters) &&
      body.matchedRoadDistanceMeters >= 0
      ? body.matchedRoadDistanceMeters : null,
    unmatchedObservationCount: Number.isSafeInteger(body.unmatchedObservationCount)
      ? body.unmatchedObservationCount : 0,
    evidence: Object.freeze({ ...(body.evidence || {}) }),
    fallback: Object.freeze({
      used: body.fallback.used,
      reason: typeof body.fallback.reason === "string" ? body.fallback.reason : null,
    }),
  });
}

export async function loadGpsReplayGeometrySnapshot({ vehicleId, from, to, provider = "osrm", signal, client = api } = {}) {
  const rangeFrom = validDate(from);
  const rangeTo = validDate(to);
  if (typeof vehicleId !== "string" || !vehicleId || !rangeFrom || !rangeTo || rangeFrom >= rangeTo ||
      rangeTo - rangeFrom > MAX_MATCHED_REPLAY_RANGE_MS || !["raw_gps", "osrm"].includes(provider)) {
    throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_RANGE_INVALID");
  }
  const response = await client.get(`/gps/vehicles/${vehicleId}/replay-geometry`, {
    params: { from: rangeFrom.toISOString(), to: rangeTo.toISOString(), provider }, signal,
  });
  const body = response?.data;
  if (!body || !Array.isArray(body.observations)) {
    throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_CONTRACT_INVALID");
  }
  const observations = body.observations.map((item, index) => normalizeReplayObservation(item, index));
  if (observations.some((item) => item.recordedAt < rangeFrom || item.recordedAt > rangeTo)) {
    throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_ORDER_INVALID");
  }
  for (let index = 1; index < observations.length; index += 1) {
    if (observations[index].recordedAt < observations[index - 1].recordedAt) {
      throw new GpsReplayGeometrySnapshotError("GPS_REPLAY_GEOMETRY_ORDER_INVALID");
    }
  }
  const contract = normalizeContract(body, observations, rangeFrom, rangeTo);
  return Object.freeze({
    source: "physical",
    range: Object.freeze({ from: rangeFrom, to: rangeTo }),
    observations: Object.freeze(observations),
    pageCount: 1,
    geometryContract: contract,
  });
}
